import React from 'react';
import styled, { css } from 'styled-components';
import PropTypes from 'prop-types';

const styles = {
  primary: css`
    background: #fff;
    box-shadow: 0 0 12px rgba(0, 0, 0, 0.05), 0 2px 6px rgba(0, 0, 0, 0.08);
    color: var(--body-text-color);
  `,
  secondary: css`
    background: var(--nude-200);
    color: var(--body-text-color);
  `,
  selected: css`
    background: #fff;
    border: solid 2px var(--primary-500);
    color: var(--body-text-color);
    position: relative;

    &::before {
      border: solid 3px #fff;
      border-top: none;
      border-right: none;
      content: '';
      height: 5px;
      left: 0px;
      position: absolute;
      top: -1px;
      transform: translate(-50%, -50%) rotate(-45deg);
      width: 11px;
      z-index: 99;
    }

    &::after {
      align-items: center;
      background: var(--primary-500);
      border-radius: 24px;
      color: #fff;
      content: '';
      display: flex;
      height: 24px;
      justify-content: center;
      left: -12px;
      position: absolute;
      top: -12px;
      width: 24px;
    }
  `,
  clear: css`
    background: transparent;
    box-shadow: none;
  `,
  disabled: css`
    background: #f9f9f9;
    box-shadow: 0 1px 6px rgba(0, 0, 0, 0.08);
  `,
};

const linkHoverStyles = {
  primary: css`
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.075), 0 4px 12px rgba(0, 0, 0, 0.1);
  `,
  secondary: css``,
  selected: css``,
  clear: css``,
  disabled: css``,
};

const titleStyles = {
  md: css`
    font-size: 15px;
    font-weight: bold;
    letter-spacing: 0;
    line-height: 26px;
    margin: 0;
    text-transform: uppercase;
  `,
  lg: css`
    font-size: 18px;
    line-height: 1.5;
    margin: 0;
  `,
};

const headerStyles = {
  md: css`
    margin-bottom: 12px;
  `,
  lg: css`
    margin-bottom: 16px;
  `,
};

const CardEl = styled.div`
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  padding: var(--card-padding);
  width: 100%;

  ${({ visualStyle = 'primary' }) => styles[visualStyle]};

  @media (max-width: 600px) {
    padding: 12px;
  }
`;

const Card = ({ children, ...props }) => {
  return <CardEl {...props}>{children}</CardEl>;
};

export const CardLink = styled(CardEl).attrs({ as: 'a' })`
  cursor: pointer;

  &:hover {
    transition: all 0.3s ease;
    ${({ visualStyle = 'primary' }) => linkHoverStyles[visualStyle]};

    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
      text-decoration: underline;
    }
  }
`;

const Title = styled.h3`
  color: var(--header-text-color);
  ${({ size = 'md' }) => titleStyles[size]}
`;

Card.Title = Title;

const Header = styled.div`
  ${({ size = 'md' }) => headerStyles[size]}
`;

Card.Header = Header;

const Breadcrumb = styled.ul`
  align-items: center;
  display: flex;
  flex-direction: row;
  gap: 12px;
  justify-content: center;
  list-style: none;
  margin: 0 0 var(--card-padding);
  padding: 0;
`;

const BreadcrumbItem = styled.li`
  color: var(--deemphasized-text-color);
  cursor: ${p => (p.onClick ? 'pointer' : 'default')};
  font-size: 14px;

  & + &::before {
    content: '›';
    font-weight: normal;
    margin-right: 12px;
    color: var(--body-text-color);
  }

  &:last-child {
    color: var(--body-text-color);
    font-weight: bold;
  }
`;

Card.Breadcrumb = Breadcrumb;
Card.Breadcrumb.Item = BreadcrumbItem;

Card.Body = styled.div`
  color: #555;
  font-size: 14px;
  a {
    color: currentColor;
  }

  & + ${Card.Title} {
    margin-top: 1em;
    padding-top: 1em;
    border-top: 1px solid var(--content-separator-medium);
  }
`;

Card.Actions = styled.div`
  padding-top: 12px;
  display: grid;
  grid-gap: 12px;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));

  // The below styles are untested, and should be treated as if they can go away at any point
  max-width: 500px;
  align-self: center;
`;

Card.displayName = 'Card';

Card.propTypes = {
  visualStyle: PropTypes.oneOf(Object.keys(styles)),
};

export default Card;
